import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { ApiService } from 'src/app/services/api.service';
import { RbacService } from 'src/app/services/rbac.service';
import { DebounceService } from 'src/app/core/services/debounce.service';
import { PaginationHelperService } from 'src/app/services/pagination-helper.service';
import { PopUpDialogService } from 'src/app/services/popup-dialog.service';
import { SharedComponentsModule } from 'src/app/shared/modules/shared-component.module';
import { SharedPipeModule } from 'src/app/shared/modules/shared-pipe.module';
import { SharedDirectivesModule } from 'src/app/shared/modules/shared-directives.module';
import { NgxSpinnerModule } from 'ngx-spinner';
import { TableControlsComponent } from 'src/app/shared/component/table-controls/table-controls.component';
import { PaginationComponent } from 'src/app/shared/component/pagination/pagination.component';
import { MegapayLoaderComponent } from 'src/app/shared/component/megapay-loader/megapay-loader.component';
import { MatDialog } from '@angular/material/dialog';
import { CreateEditDisbursementModalComponent } from './create-edit-disbursement-modal/create-edit-disbursement-modal.component';
import * as vo from 'src/app/core/model/server/vo/_index';
import { SKU_TAB_OPTIONS } from 'src/app/lib/constants/tabs.constants';
import {
  DISBURSEMENT_TABLE_SUB_HEADERS,
  DISBURSEMENT_INSTITUTION_TYPE_OPTIONS,
} from 'src/app/lib/constants/disbursement.constants';
import { Permission } from 'src/app/lib/rbac.config';

@Component({
  selector: 'app-sku-disbursement',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatTooltipModule,
    NgxSpinnerModule,
    SharedComponentsModule,
    SharedPipeModule,
    SharedDirectivesModule,
    TableControlsComponent,
    PaginationComponent,
    MegapayLoaderComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './sku-disbursement.component.html',
  styleUrl: './sku-disbursement.component.scss'
})
export class SkuDisbursementComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  // ─── Table ─────────────────────────────────────────────────────────────────
  tabs           = SKU_TAB_OPTIONS;
  tableSubHeader = DISBURSEMENT_TABLE_SUB_HEADERS;
  institutions: vo.IDisbursementInstitution[] = [];
  isLoading = false;

  // ─── Filters (Code / Name / Institution Type) ──────────────────────────────
  filterForm!: UntypedFormGroup;
  institutionTypeOptions = DISBURSEMENT_INSTITUTION_TYPE_OPTIONS;
  institutionType: string | null = null;
  institutionType_text = '';
  private appliedFilters = { code: '', name: '', institutionType: null as string | null };

  // ─── Pagination ────────────────────────────────────────────────────────────
  size         = 10;
  total_pages: any;
  current_page = 1;
  pages_array: number[] = [];
  total: any;

  // ─── Search & Limit ────────────────────────────────────────────────────────
  searchKey: any = null;
  limitKey: any  = null;
  search_keyword = '';
  private pendingSearch = '';
  private pendingLimit  = 10;

  // ─── Sorting ───────────────────────────────────────────────────────────────
  sort_column = 'createDate';
  sort_type: 'ASC' | 'DESC' = 'DESC';

  constructor(
    @Inject(PopUpDialogService) private popUpDialog: PopUpDialogService,
    private fb: UntypedFormBuilder,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private apiService: ApiService,
    private rbac: RbacService,
    private dialog: MatDialog,
    public debounce: DebounceService,
    public paginationHelper: PaginationHelperService,
  ) {
    this.filterForm = this.fb.group({ code: [''], name: [''] });

    this.searchKey = this.debounce.debounce(() => {
      this.current_page   = 1;
      this.search_keyword = this.pendingSearch;
      this.loadInstitutions();
    }, 500);

    this.limitKey = this.debounce.debounce(() => {
      this.current_page = 1;
      this.size         = this.pendingLimit;
      this.loadInstitutions();
    }, 500);
  }

  // ─── Lifecycle ─────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.tabs = SKU_TAB_OPTIONS.filter(tab => !tab['permission'] || this.rbac.hasPermission(tab['permission'] as Permission));
    this.loadInstitutions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Request builder (shared by list + export) ─────────────────────────────
  private buildRequest(): vo.GetDisbursementInstitutionsRequest {
    const { code, name, institutionType } = this.appliedFilters;
    const request: vo.GetDisbursementInstitutionsRequest = {
      sortBy: { [this.sort_column]: this.sort_type },
    };
    if (code?.trim())     { request.code = code.trim(); }
    if (name?.trim())     { request.name = name.trim(); }
    if (institutionType)  { request.institutionType = institutionType; }
    if (this.search_keyword) { request.search = this.search_keyword; }
    return request;
  }

  // ─── List ──────────────────────────────────────────────────────────────────
  loadInstitutions(): void {
    this.spinner.show().catch(console.error);
    this.isLoading = true;
    this.pages_array = [];

    this.apiService.getDisbursementInstitutions(this.buildRequest(), this.current_page, this.size)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false; 
          this.spinner.hide().catch(console.error)
        }),
      )
      .subscribe({
        next: (response) => {
          if (response.status === 200) {
            const page = response.result;
            this.institutions  = page.data;
            this.pages_array   = this.paginationHelper.generatePageArray(this.current_page, page.totalPages);
            this.total_pages   = page.totalPages;
            this.current_page  = page.currentPage;
            this.total         = page.totalItems;
          }
        },
        error: (err) => {
          console.error(err);
          this.toastr.error(
            'Please refresh or contact the developer if the error still occurs.',
            'Failed Fetching Disbursement Institutions'
          );
        },
      });
  }

  // ─── Filters ───────────────────────────────────────────────────────────────
  setInstitutionType(text: string, value: any): void {
    this.institutionType      = value;
    this.institutionType_text = value ? text : '';
  }

  searchList(): void {
    this.current_page   = 1;
    this.appliedFilters = {
      code: this.filterForm.value.code,
      name: this.filterForm.value.name,
      institutionType: this.institutionType,
    };
    this.loadInstitutions();

    // clear the inputs after searching
    this.filterForm.reset({ code: '', name: '' });
    this.institutionType      = null;
    this.institutionType_text = '';
  }

  // ─── Dialogs ───────────────────────────────────────────────────────────────
  openCreateDialog(): void {
    this.dialog.open(CreateEditDisbursementModalComponent, {
      width: '55vw',
      maxWidth: '820px',
      maxHeight: '90vh',
      panelClass: 'create-edit-sku-dialog',
      data: { mode: 'create' },
      disableClose: true,
    }).afterClosed().subscribe((saved: boolean) => { if (saved) { this.loadInstitutions(); } });
  }

  openEditDialog(item: vo.IDisbursementInstitution): void {
    this.dialog.open(CreateEditDisbursementModalComponent, {
      width: '55vw',
      maxWidth: '820px',
      maxHeight: '90vh',
      panelClass: 'create-edit-sku-dialog',
      data: { mode: 'edit', item },
      disableClose: true,
    }).afterClosed().subscribe((saved: boolean) => { if (saved) { this.loadInstitutions(); } });
  }

  openImageViewer(inst: vo.IDisbursementInstitution): void {
    if (!inst.imageUrl) { return; }
    this.popUpDialog.openImagePreviewWithURLDialog(inst.imageUrl, 'Image URL', this.getFileName(inst.imageUrl) ?? undefined);
  }

  // ─── Enable / Disable ──────────────────────────────────────────────────────
  confirmToggleStatus(inst: vo.IDisbursementInstitution, action: 'enable' | 'disable'): void {
    const isEnable = action === 'enable';

    this.popUpDialog.openStatusActionDialog(
      `Are you sure you want to ${action} this account?`,
      isEnable ? 'ENABLE' : 'DISABLE',
      'CANCEL',
      () => this.toggleStatus(inst, action),
    );
  }

  async toggleStatus(inst: vo.IDisbursementInstitution, action: 'enable' | 'disable'): Promise<void> {
    this.spinner.show().catch(console.error);
    let success = false;

    try {
      const formData = new FormData();
      formData.append('uid', String(inst.uid));
      formData.append('action', action);

      const response = await this.apiService.updateDisbursementInstitution(formData);

      if (response.status === 200) {
        this.toastr.success(
          action === 'enable' ? 'Institution enabled successfully.' : 'Institution disabled successfully.',
          'Success!'
        );
        success = true;
      } else {
        this.toastr.error(response.message || 'Failed to update status.', 'Something went wrong!');
      }
    } catch (error: any) {
      this.toastr.error(error?.error?.message || error?.message || 'Something went wrong!', 'Error!');
    } finally {
      this.spinner.hide().catch(console.error);
    }

    if (success) { this.loadInstitutions(); }
  }


  // ─── Table Controls ────────────────────────────────────────────────────────
  onLimitChangeFromComponent(event: any): void {
    this.pendingLimit = Number(event.target.value);
    this.limitKey();
  }

  onSearchKeyupFromComponent(event: any): void {
    this.pendingSearch = event.target.value;
    this.searchKey();
  }

  keyDownFunction(event: any): void {
    if (event.keyCode === 13) {
      this.current_page   = 1;
      this.search_keyword = this.pendingSearch;
      this.loadInstitutions();
    }
  }

  sortList(sort_column: string, sort_type: 'ASC' | 'DESC'): void {
    const { column, type } = this.paginationHelper.toggleSort(this.sort_column, this.sort_type, sort_column, sort_type);
    this.sort_column = column;
    this.sort_type   = type;
    if (this.institutions) { this.loadInstitutions(); }
  }

  // ─── Pagination ────────────────────────────────────────────────────────────
  setPage(num: number): void {
    this.current_page = num;
    this.loadInstitutions();
  }

  nextPage(): void {
    if (this.current_page < Number(this.total_pages)) {
      this.current_page++;
      this.loadInstitutions();
    }
  }

  prevPage(): void {
    if (this.current_page > 1) {
      this.current_page--;
      this.loadInstitutions();
    }
  }

  // ─── Helpers ───────────────────────────────────────────────────────────────
  getFileName(url: string | null): string | null {
    return url ? url.split('/').pop() ?? null : null;
  }

  // ─── Export ────────────────────────────────────────────────────────────────
  print(): void {
    this.spinner.show().catch(console.error);

    this.apiService.exportDisbursementInstitutions(this.buildRequest())
      .pipe(finalize(() => this.spinner.hide().catch(console.error)))
      .subscribe({
        next: (blob: Blob) => {
          const a = document.createElement('a');
          const objectUrl = URL.createObjectURL(blob);
          a.href = objectUrl;
          a.download = 'disbursement_institutions.xlsx';
          a.click();
          URL.revokeObjectURL(objectUrl);
        },
        error: () => this.toastr.error('Failed to export disbursement institutions.', 'Export Error'),
      });
  }
}
